/**
 * `FileBracket` — `[ ]`, the mark that files a record. `docs/design-direction.md`
 * §8.5, §12.21.
 *
 * ## Why it lives in `components/` and not on the shelf
 *
 * It used to be declared inside `screens/MyCrate.tsx`, which meant the only place a
 * record could be filed was the shelf of records already filed. "Моё" could therefore
 * be filled only from itself — i.e. never. Moving the control down a layer is what lets
 * `TrackRow` carry it, so a record can be filed from the crate, a search result, an
 * artist or a tag, which is the whole point of the feature.
 *
 * `MyCrate` still re-exports {@link FileBracket} and {@link FILE_LABEL_RU} from here, so
 * every existing import keeps working and there is exactly one definition.
 *
 * ## The mark is two pseudo-elements, and it is never a heart
 *
 * 1px strokes that fill solid `--accent` when the record is filed. No glyph, so it
 * cannot fall back to a font that lacks it. The mark is `aria-hidden`: the button's
 * Russian name and its `aria-pressed` carry the whole meaning. **There is no heart
 * anywhere in this application** (§12.21).
 */

import type { JSX } from 'react';

import { cx } from './primitives/Rule';

import styles from './components.module.css';

/** The button's accessible name. Russian, like every control in the app (§9.4). */
export const FILE_LABEL_RU = 'Отправить в ящик';

export interface FileBracketProps {
  readonly filed: boolean;
  readonly onToggle: () => void;
  readonly className?: string | undefined;
}

export function FileBracket({ filed, onToggle, className }: FileBracketProps): JSX.Element {
  return (
    <button
      type="button"
      className={cx(styles.bracket, className)}
      aria-label={FILE_LABEL_RU}
      aria-pressed={filed}
      data-filed={filed ? 'true' : 'false'}
      onClick={onToggle}
    >
      <span className={styles.bracketMark} aria-hidden="true" />
    </button>
  );
}
